/* Primora - shared scroll reveal
   REVISION a
   Revision:      a - 10 Sep 2026
   Goes in:       assets/primora-reveal.js
   Pairs with:    every section that emits a [data-primora-reveal] attribute
                  and every stylesheet gating on html.primora-reveal-on

   PORTED FROM aurmina-reveal.js rev b.

   THE LOCKSTEP RENAME. Four names form one contract, and they appear in this
   file and in every dependent stylesheet:

       html.primora-reveal-on     the gate class, set below before paint
       [data-primora-reveal]      the attribute sections emit
       .is-revealed               the class this script adds when observed
       window.__primoraReveal     the once-per-page guard

   At rev a the dependents are section-primora-prose-band.css and
   section-primora-card-grid.css. Rename any one of these in one place only
   and every gated section stops animating - content stays visible, nothing
   errors, nothing reaches the console. Rename in one pass or not at all.

   The fail-safe direction is deliberate: the start state lives behind the gate
   class, so if this script never loads, content renders visible and
   unanimated rather than permanently invisible.

   The counter path below has no consumers yet. It stays because it costs
   nothing and the landing page's figures are unresolved; wire nothing to it
   until the trial numbers are settled.

   Stylesheets additionally gate on (any-pointer: fine), so touch devices get
   no reveal animation at all. That is Dwell's convention and is intended.

   Vanilla, no dependencies. Respects prefers-reduced-motion. Re-binds on
   shopify:section:load so sections added mid-edit in the theme editor are
   observed rather than staying invisible.
   ========================================================================== */

(function () {
  'use strict';

  /* Guard first: the script tag is emitted per section, so this file is
     executed once per section on the page. */
  if (window.__primoraReveal) return;
  window.__primoraReveal = true;

  /* The gate every section stylesheet waits for. Set before paint so the
     start state applies to markup that has not rendered yet. */
  document.documentElement.classList.add('primora-reveal-on');

  var REDUCED =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function showAll(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.add('is-revealed');
    }
  }

  function bind(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-primora-reveal]:not(.is-revealed)');
    if (!nodes.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      showAll(nodes);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.14, rootMargin: '0px 0px -40px 0px' }
    );

    for (var i = 0; i < nodes.length; i++) {
      observer.observe(nodes[i]);
    }
  }

  /* Count-up for numeric stats. Opt-in via data-primora-count="245". */
  function bindCounters(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-primora-count]:not(.is-counted)');

    for (var i = 0; i < nodes.length; i++) {
      (function (el) {
        var target = parseFloat(el.getAttribute('data-primora-count'));
        if (isNaN(target)) return;

        el.classList.add('is-counted');

        if (REDUCED || !('IntersectionObserver' in window)) {
          el.textContent = target;
          return;
        }

        var run = function () {
          var start = null;
          var step = function (ts) {
            if (!start) start = ts;
            var p = Math.min((ts - start) / 1400, 1);
            var eased = p * p * (3 - 2 * p);
            el.textContent = Math.floor(eased * target);
            if (p < 1) {
              window.requestAnimationFrame(step);
            } else {
              el.textContent = target;
            }
          };
          window.requestAnimationFrame(step);
        };

        var io = new IntersectionObserver(
          function (entries) {
            if (entries[0].isIntersecting) {
              run();
              io.disconnect();
            }
          },
          { threshold: 0.5 }
        );
        io.observe(el);
      })(nodes[i]);
    }
  }

  function init(root) {
    bind(root);
    bindCounters(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
